import axios from "axios";
import * as AuthService from "./AuthService";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { toast } from "react-toastify";
import { isValid, format, parse } from "date-fns";
import React from "react";
import formStyles from "../../assets/scss/common/formStyle.module.scss";
import { buildNavData } from "helpers/buildNavData";
import { DASHBOARD_PATHS } from "Routes/paths";


/* ---------- dayjs setup ---------- */

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

/* ---------- Types ---------- */

export interface CurrentUser {
  userid?: number;
  username?: string;

  paginationlength?: number;
  userdisplayname?: string;
  versionid?: number;
  versionname?: string;
  iscompanysettingrr?: boolean;
  [key: string]: unknown;
}

type ToastType = "success" | "error";

/* ---------- Local Storage ---------- */

export const clearSessionStorage = (): void => {
  const baseUrl = sessionStorage.getItem("baseUrl");
  const filebaseUrl = sessionStorage.getItem("filebaseUrl");
  const reportUrl = sessionStorage.getItem("reportUrl");
  const iPAddress = sessionStorage.getItem("iPAddress");
  const versionName = sessionStorage.getItem("VersionName");
  const tab = sessionStorage.getItem("REMS_Tab");
  const isIdealTimeOut = sessionStorage.getItem("IsIdealTimeOut");

  sessionStorage.removeItem("activestudyid")
  sessionStorage.removeItem("activevolunteer")
  sessionStorage.removeItem("activevolunteerid")
  sessionStorage.removeItem("volunteerName")
  sessionStorage.removeItem("currentPage")
  sessionStorage.removeItem("currentUser")
  sessionStorage.removeItem("token")
  sessionStorage.removeItem("studycode")
  sessionStorage.removeItem("role")
  sessionStorage.removeItem("returnForm")
  sessionStorage.removeItem("volunteername")
  sessionStorage.removeItem("volunteerassignmentid")
  sessionStorage.removeItem("globalstudyname")
  sessionStorage.removeItem("globalstudyid")
  sessionStorage.removeItem("active_volunteer_screening_status")

  // Signal StudyProvider to reset in-memory selectedStudy → null immediately
  window.dispatchEvent(new Event("clearGlobalStudy"));

  // sessionStorage.clear();

  if (baseUrl) sessionStorage.setItem("baseUrl", baseUrl);
  if (filebaseUrl) sessionStorage.setItem("filebaseUrl", filebaseUrl);
  if (reportUrl) sessionStorage.setItem("reportUrl", reportUrl);
  if (iPAddress) sessionStorage.setItem("iPAddress", iPAddress);
  if (versionName) sessionStorage.setItem("VersionName", versionName);
  if (tab) sessionStorage.setItem("REMS_Tab", tab);
  if (isIdealTimeOut)
    sessionStorage.setItem("IsIdealTimeOut", isIdealTimeOut);
};


/* ---------- Auth ---------- */

export const userLogout = (navigate: (path: string) => void): void => {
  const currentUser = sessionStorage.getItem("currentUser");

  if (currentUser) {
    const parsed: CurrentUser = JSON.parse(currentUser);
    AuthService.userLogout(String(parsed.username)).then(() => {
      navigate("/logout");
    });
  } else {
    toast.error("Something wrong!", { autoClose: 3000 });
  }
};

export const GetUserData = (): CurrentUser | null => {
  const user = sessionStorage.getItem("currentUser");
  return user ? (JSON.parse(user) as CurrentUser) : null;
};

export const GetSystemSettingValue = (settingNameOrId: string | number): string | null => {
  const user = GetUserData();
  if (!user || !Array.isArray(user.settings)) return null;

  const searchStr = String(settingNameOrId).toLowerCase();

  let setting = user.settings.find(
    (s: any) =>
      String(s.settingmasterid) === String(settingNameOrId) ||
      (s.settingname && s.settingname.toLowerCase() === searchStr)
  );

  if (!setting) {
    setting = user.settings.find(
      (s: any) => s.settingname && s.settingname.toLowerCase().includes(searchStr)
    );
  }

  return setting ? setting.settingvalue : null;
};

/* ---------- Toast ---------- */

export const showToastMessage = (
  type: ToastType,
  message: string
): void => {
  if (type === "success") toast.success(message, { autoClose: 2000 });
  else toast.error(message, { autoClose: 2000 });
};





//--------------------------------------------------------------------------------------
//--> Normalize From Value
//--------------------------------------------------------------------------------------



/* ---------- Pagination ---------- */

export const perPageData: number =
  GetUserData()?.paginationlength ?? 10;

export const refreshPagination = <T>(
  pageData: T[],
  currentPage: number
): T[] => {
  const indexOfLast = currentPage * perPageData;
  const indexOfFirst = indexOfLast - perPageData;
  return pageData?.slice(indexOfFirst, indexOfLast);
};

/* ---------- Date Helpers ---------- */

export const dateFormatOptions = {
  dateFormat: "Y-m-d",
  altFormat: "Y-m-d",
  altInput: true,
};


export const DateFormat = (
  date: Date | string | null
): string | null => {
  if (!date) return null;

  // already Date object
  if (date instanceof Date) {
    return isValid(date)
      ? format(date, "yyyy-MM-dd")
      : null;
  }

  // string input
  if (typeof date === "string") {

    // if already ISO like 2026-12-21
    let parsed = parse(date, "yyyy-MM-dd", new Date());

    // fallback dd-MM-yyyy like 21-12-2026
    if (!isValid(parsed)) {
      parsed = parse(date, "dd-MM-yyyy", new Date());
    }

    if (!isValid(parsed)) return null;

    return format(parsed, "yyyy-MM-dd");
  }

  return null;
};


export const pastAndCurrentDatesOnly = {
  dateFormat: "Y-m-d",
  altFormat: "Y-m-d",
  altInput: true,
  maxDate: "today",
};
export const formatDateSplit = (date: any) => {
  if (!date) return null;

  if (date instanceof Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  if (typeof date === "string") {
    return date.includes("T") ? date.split("T")[0] : date;
  }

  return null;
};

/* ---------- Sorting ---------- */

let sortBy = false;

export const setOrder = <T,>(
  data: T[],
  value: keyof T,
  setSelectAllData?: React.Dispatch<React.SetStateAction<T[]>>
): void => {
  if (data && data.length > 0) {
    sortBy = !sortBy;
    // sorting logic intentionally commented
  }
};

/* ---------- Formik Helper ---------- */

export const handleChange = (
  formik: { handleChange: (e: React.ChangeEvent<any>) => void },
  e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
): void => {
  const { value } = e.target;

  if (value.length === 1 && value === "\n") return;
  if (value.trim() === "" && value.includes(" ")) return;

  formik.handleChange(e);
};



// export const parseLocalDate = (value: Date | string | null | undefined): Date | null => {
//   if (!value) return null;
//   if (value instanceof Date) {
//     return isNaN(value.getTime()) ? null : value;
//   }
//   if (typeof value === "string") {
//     const cleanStr = value.trim();
//     // YYYY-MM-DD
//     const yyyymmddMatch = cleanStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
//     if (yyyymmddMatch) {
//       const parsed = dayjs(yyyymmddMatch[0], "YYYY-MM-DD");
//       return parsed.isValid() ? parsed.toDate() : null;
//     }
//     // DD-MM-YYYY
//     const ddmmyyyyMatch = cleanStr.match(/^(\d{2})-(\d{2})-(\d{4})/);
//     if (ddmmyyyyMatch) {
//       const parsed = dayjs(ddmmyyyyMatch[0], "DD-MM-YYYY");
//       return parsed.isValid() ? parsed.toDate() : null;
//     }
//     const parsed = dayjs(cleanStr);
//     return parsed.isValid() ? parsed.toDate() : null;
//   }
//   return null;
// };

export const parseLocalDate = (
  value: Date | string | null | undefined
): Date | null => {
  if (!value) return null;

  // Date object
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  const cleanStr = value.trim();

  if (!cleanStr) return null;

  // --------------------------------------------------
  // 1. Datetime with timezone
  // Example:
  // 2026-08-27T08:23:55.435522Z
  // 2026-09-01T04:26:01.805128+00:00
  // 2026-09-01 04:26:01.805128+00
  // --------------------------------------------------
  const hasTimeZone =
    /^\d{4}-\d{2}-\d{2}[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2}|[+-]\d{2})$/.test(
      cleanStr
    );

  if (hasTimeZone) {
    // PostgreSQL may return +00 instead of +00:00
    const normalized = cleanStr.replace(
      /([+-]\d{2})$/,
      "$1:00"
    );

    const parsed = new Date(normalized);

    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // --------------------------------------------------
  // 2. YYYY-MM-DD
  // This is a date WITHOUT timezone.
  // Keep it as a local date.
  // --------------------------------------------------
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) {
    const parsed = dayjs(cleanStr, "YYYY-MM-DD", true);

    return parsed.isValid() ? parsed.toDate() : null;
  }

  // --------------------------------------------------
  // 3. DD-MM-YYYY
  // --------------------------------------------------
  if (/^\d{2}-\d{2}-\d{4}$/.test(cleanStr)) {
    const parsed = dayjs(cleanStr, "DD-MM-YYYY", true);

    return parsed.isValid() ? parsed.toDate() : null;
  }

  // --------------------------------------------------
  // 4. Fallback
  // --------------------------------------------------
  const parsed = dayjs(cleanStr);

  return parsed.isValid() ? parsed.toDate() : null;
};


let globalDateFormat = "";

export const setGlobalDateFormat = (format: string) => {
  if (format) {
    globalDateFormat = format;
  }
};

export const formatDate = (value?: Date | string) => {
  if (!value) return "-";
  const d = parseLocalDate(value);

  if (!d || isNaN(d.getTime())) return "-";

  const rawFormat = globalDateFormat;

  const pattern = rawFormat
    .replace(/yyyy/gi, "YYYY")
    .replace(/yy/gi, "YY")
    .replace(/mm/g, "MM")
    .replace(/dd/gi, "DD")
    .replace(/Mon/g, "MMM");

  return dayjs(d).format(pattern);
};

/** Centralized 24-hour time conversion helper function */
export const formatTime24Hour = (timeStr?: string | null, includeSeconds: boolean = true): string => {
  if (!timeStr) return "-";
  const cleanStr = String(timeStr).trim();
  if (cleanStr === "" || cleanStr === "--" || cleanStr === "-") return "-";

  // Check if it has AM/PM (12-hour format)
  const ampmMatch = cleanStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)$/i);
  if (ampmMatch) {
    let [_, hStr, mStr, sStr, ampm] = ampmMatch;
    let h = parseInt(hStr, 10);
    const m = mStr;
    const s = sStr || "00";
    const isPM = ampm.toUpperCase() === "PM";
    const isAM = ampm.toUpperCase() === "AM";

    if (isPM && h < 12) h += 12;
    if (isAM && h === 12) h = 0;

    const hh = String(h).padStart(2, "0");
    return includeSeconds ? `${hh}:${m}:${s}` : `${hh}:${m}`;
  }

  // Check if already in 24-hour format "HH:mm:ss" or "HH:mm"
  const time24Match = cleanStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (time24Match) {
    const [_, hStr, mStr, sStr] = time24Match;
    const hh = String(parseInt(hStr, 10)).padStart(2, "0");
    const m = mStr;
    const s = sStr || "00";
    return includeSeconds ? `${hh}:${m}:${s}` : `${hh}:${m}`;
  }

  // Fallback: parse as Date
  try {
    const date = new Date(cleanStr);
    if (!isNaN(date.getTime())) {
      const hh = String(date.getHours()).padStart(2, "0");
      const mm = String(date.getMinutes()).padStart(2, "0");
      const ss = String(date.getSeconds()).padStart(2, "0");
      return includeSeconds ? `${hh}:${mm}:${ss}` : `${hh}:${mm}`;
    }
  } catch (e) {
    // ignore
  }

  return cleanStr;
};

/** Get current system time in 24-hour format ("HH:mm:ss" or "HH:mm") */
export const getNow24Hour = (includeSeconds: boolean = true): string => {
  const now = new Date();
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  if (!includeSeconds) return `${hh}:${mm}`;
  const ss = String(now.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};




/* --------------------------------
Access Control & Helper Utilities
--------------------------------- */

export const checkModuleVisibleDirect = (
  allowedModules: any[],
  moduleIdentifier?: string
): boolean => {
  if (!moduleIdentifier) return true;

  const normalizedIdentifier = moduleIdentifier.toLowerCase().trim();
  const alwaysAllowedModules = ["global study access"];

  if (alwaysAllowedModules.includes(normalizedIdentifier)) {
    return true;
  }

  if (!Array.isArray(allowedModules) || allowedModules.length === 0) {
    return true;
  }

  const activeKeys = new Set<string>();
  const traverse = (mod: any) => {
    if (!mod) return;

    if (mod.featurecode) {
      activeKeys.add(String(mod.featurecode).toLowerCase().trim());
    }
    if (mod.featurename) {
      activeKeys.add(String(mod.featurename).toLowerCase().trim());
    }

    if (Array.isArray(mod.dependentfeatures)) {
      mod.dependentfeatures.forEach((dep: any) => traverse(dep));
    }
  };

  allowedModules.forEach((mod) => traverse(mod));

  return activeKeys.has(normalizedIdentifier);
};

export const checkPermissionDirect = (
  rights: Record<string, string[]>,
  module?: string,
  action?: string
): boolean => {
  if (!module) return true;
  if (!rights) return false;

  const modules = module.split(",").map((m) => m.trim());
  return modules.some((mod) => {
    const [actualMod, actionPrefix] = mod.split(":");
    const moduleRights = rights[actualMod];
    if (!moduleRights) return false;
    if (!action) return true;
    const actions = action.split(",").map((act) => act.trim());
    return actions.some((act) => {
      const mappedAction = actionPrefix ? `${actionPrefix}-${act}` : act;
      return moduleRights.includes(mappedAction);
    });
  });
};

export const getFirstAccessiblePath = (
  rights?: Record<string, string[]>,
  allowedModules?: any[],
  checkHelpers?: {
    isModuleVisible?: (formname: string) => boolean;
    hasPermission?: (module: string) => boolean;
  }
): string => {
  const userData = GetUserData() as any;
  const userRights = rights || userData?.rights || {};
  const userAllowedModules = allowedModules || userData?.allowed_modules || [];

  const navData = buildNavData();

  const isAccessible = (item: any): boolean => {
    let isVisible = true;
    let hasRights = true;

    if (checkHelpers?.isModuleVisible) {
      isVisible = !item.formname || checkHelpers.isModuleVisible(item.formname);
    } else {
      isVisible = !item.formname || checkModuleVisibleDirect(userAllowedModules, item.formname);
    }

    if (checkHelpers?.hasPermission) {
      hasRights = !item.module || checkHelpers.hasPermission(item.module);
    } else {
      hasRights = !item.module || checkPermissionDirect(userRights, item.module);
    }

    return isVisible && hasRights;
  };

  // Recursively finds the first valid leaf URL in the item hierarchy
  const findLeafUrl = (item: any): string | null => {
    // 1. Check subchildren first
    if (Array.isArray(item.lstsubchildform) && item.lstsubchildform.length > 0) {
      for (const subChild of item.lstsubchildform) {
        const url = findLeafUrl(subChild);
        if (url) return url;
      }
      return null;
    }

    // 2. Check 1st children next
    if (Array.isArray(item.lstchildform) && item.lstchildform.length > 0) {
      for (const child of item.lstchildform) {
        const url = findLeafUrl(child);
        if (url) return url;
      }
      return null;
    }

    // 3. Leaf item (no children/subchildren) - check accessibility & return formurl
    if (item.formurl && isAccessible(item)) {
      return item.formurl;
    }

    return null;
  };

  // Traverse main menu items in buildNavData order
  for (const mainItem of navData) {
    const url = findLeafUrl(mainItem);
    if (url) return url;
  }

  return DASHBOARD_PATHS.DEFAULT;
};

 